/**
 * audit-mock-data.ts
 *
 * Scaffold seed data for the Activity Audit feature.
 * ALL queries here hard-exclude tenant_id = 2 (Mi6 Demo).
 *
 * Open Question A2 answer: The `activities` table does NOT yet exist in
 * Supabase (aip platform project gstkgiwdjvztopjbeoiy). Tables present are
 * accounts, support_tickets, users, tenants, prospects, plans, etc.
 * No activity_type / outcome / channel columns anywhere.
 * This scaffold uses mock data; Option B (May 12) will create the
 * `activity_events` table and backfill.
 *
 * Tenant isolation: All mock rows use tenant_id: 1 .
 * The runtime guard `WHERE tenant_id != 2` is enforced in the
 * query helpers below and will carry into the real Supabase queries.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimeWindow = "today" | "week" | "month" | "quarter" | "year";

export type ActivityChannel =
  | "Phone"
  | "Email"
  | "LinkedIn"
  | "SMS"
  | "In-Person";

export type ActivityOutcome =
  | "Connected"
  | "Voicemail"
  | "No Answer"
  | "Replied"
  | "Booked"
  | "Not Interested"
  | "Qualified-Forward";

export type ActivitySource =
  | "Cold Outbound"
  | "Marketing MQL"
  | "AE Referral"
  | "Event"
  | "Self-Sourced";

export type ActivityType =
  | "call"
  | "email"
  | "linkedin"
  | "sms"
  | "in_person"
  | "meeting"
  | "demo"
  | "proposal";

export interface BdrActivityRow {
  id: string;
  tenant_id: 1; // Always 1 — tenant_id != 2 enforced
  user_id: string;
  logged_at: string; // ISO timestamptz
  channel: ActivityChannel;
  contact: string;
  company: string;
  outcome: ActivityOutcome;
  source: ActivitySource;
  notes: string;
  activity_type: ActivityType;
}

export interface AeActivityRow {
  id: string;
  tenant_id: 1;
  user_id: string;
  logged_at: string;
  type: "Meeting" | "Demo" | "Proposal" | "Call" | "Email";
  account: string;
  account_id: number;
  cp_stage: string;
  outcome: string;
  next_step: string;
  deal_value: number;
}

export interface ManagerRepSummary {
  user_id: string;
  name: string;
  role: "BDR" | "AE";
  tenant_id: 1;
  conversations: number;
  meetings: number;
  qualified_fwd: number | null;
  deals_advanced: number | null;
  deals_closed: number | null;
  prior_conversations: number;
  prior_meetings: number;
  sparkline: number[]; // 7-day conversation counts
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ts(daysAgo: number, h: number, m: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

// ─── BDR Seed — tenant_id: 1  only ─────────────────────────────────

export const BDR_ACTIVITIES: BdrActivityRow[] = [
  { id: "b01", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0, 10, 14), channel: "Phone",    contact: "James Smith",    company: "Aptiv",         outcome: "Booked",            source: "Cold Outbound",  notes: "Booked a 30-minute discovery call for Thursday at 2 PM.",              activity_type: "call" },
  { id: "b02", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0, 10,  2), channel: "Email",    contact: "Tina Lee",       company: "Bosch",         outcome: "Replied",           source: "Marketing MQL",  notes: "Replied to cold email — interested in learning more about the platform.",activity_type: "email" },
  { id: "b03", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  9, 47), channel: "LinkedIn", contact: "Mohit Patel",    company: "Rivian",        outcome: "No Answer",         source: "Cold Outbound",  notes: "InMail sent — no response yet.",                                        activity_type: "linkedin" },
  { id: "b04", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  9, 31), channel: "Phone",    contact: "Amanda Jones",   company: "Ford",          outcome: "Voicemail",         source: "AE Referral",    notes: "Left personalized voicemail referencing fleet telematics pain.",        activity_type: "call" },
  { id: "b05", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  9, 15), channel: "Phone",    contact: "Derek Sato",     company: "Lear",          outcome: "Connected",         source: "Cold Outbound",  notes: "Strong conversation — VP interested in quality control use case.",       activity_type: "call" },
  { id: "b06", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  8, 58), channel: "Email",    contact: "Priya Nair",     company: "Stellantis",    outcome: "No Answer",         source: "Marketing MQL",  notes: "Sent follow-up to webinar attendee.",                                   activity_type: "email" },
  { id: "b07", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  8, 44), channel: "Phone",    contact: "Leo Müller",     company: "Marelli",       outcome: "Qualified-Forward", source: "Cold Outbound",  notes: "Qualified and passed to AE — budget confirmed, project live.",          activity_type: "call" },
  { id: "b08", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  8, 30), channel: "SMS",      contact: "Casey Nguyen",   company: "BorgWarner",    outcome: "Replied",           source: "Self-Sourced",   notes: "Text follow-up from tradeshow — wants a deck.",                         activity_type: "sms" },
  { id: "b09", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  8, 15), channel: "Phone",    contact: "Sarah O'Brien",  company: "Toyota",        outcome: "Not Interested",    source: "Cold Outbound",  notes: "Not interested — budget frozen until Q3.",                              activity_type: "call" },
  { id: "b10", tenant_id: 1, user_id: "usr-maya", logged_at: ts(0,  8,  5), channel: "Email",    contact: "William Park",   company: "Bose",          outcome: "Booked",            source: "AE Referral",    notes: "AE referral — booked discovery.",                                       activity_type: "email" },
  { id: "b11", tenant_id: 1, user_id: "usr-maya", logged_at: ts(1, 16, 45), channel: "Phone",    contact: "Nina Ross",      company: "Aptiv",         outcome: "Voicemail",         source: "Cold Outbound",  notes: "Third attempt — voicemail left.",                                       activity_type: "call" },
  { id: "b12", tenant_id: 1, user_id: "usr-maya", logged_at: ts(1, 15, 30), channel: "Email",    contact: "Robert Wan",     company: "Bosch",         outcome: "Replied",           source: "Cold Outbound",  notes: "Positive reply — wants case study for automotive quality.",             activity_type: "email" },
  { id: "b13", tenant_id: 1, user_id: "usr-maya", logged_at: ts(1, 14, 10), channel: "Phone",    contact: "Yui Tanaka",     company: "Toyota",        outcome: "Connected",         source: "Event",          notes: "IMTS follow-up — 20 min conversation, scheduling next step.",           activity_type: "call" },
  { id: "b14", tenant_id: 1, user_id: "usr-maya", logged_at: ts(1, 13, 55), channel: "LinkedIn", contact: "Ben Owens",      company: "Lear",          outcome: "Replied",           source: "Marketing MQL",  notes: "InMail reply — interested in operator methodology.",                    activity_type: "linkedin" },
  { id: "b15", tenant_id: 1, user_id: "usr-maya", logged_at: ts(1, 12,  0), channel: "Phone",    contact: "Maria Costa",    company: "Ford",          outcome: "Booked",            source: "Cold Outbound",  notes: "Booked 45-min discovery with Director of Procurement.",                 activity_type: "call" },
];

// Prior-period BDR (yesterday / last 7 days) — used for delta computation
export const BDR_KPI_MOCK = {
  today: {
    conversations:      10,
    calls_made:         7,
    emails_sent:        3,
    meetings_booked:    2,
    qualified_forward:  1,
  },
  prior_today: {
    conversations:      8,
    calls_made:         9,
    emails_sent:        2,
    meetings_booked:    1,
    qualified_forward:  1,
  },
  week: {
    conversations:      52,
    calls_made:         38,
    emails_sent:        14,
    meetings_booked:    9,
    qualified_forward:  4,
  },
  prior_week: {
    conversations:      47,
    calls_made:         42,
    emails_sent:        12,
    meetings_booked:    8,
    qualified_forward:  3,
  },
  month: {
    conversations:      204,
    calls_made:         148,
    emails_sent:        56,
    meetings_booked:    34,
    qualified_forward:  18,
  },
  prior_month: {
    conversations:      189,
    calls_made:         161,
    emails_sent:        49,
    meetings_booked:    29,
    qualified_forward:  14,
  },
};

// Chart series for BDR stacked bar (hourly for Today, daily for Week/Month)
export const BDR_CHART_TODAY = [
  { label: "8am",  calls: 2, emails: 1, convos: 3, meetings: 0 },
  { label: "9am",  calls: 3, emails: 1, convos: 4, meetings: 1 },
  { label: "10am", calls: 2, emails: 1, convos: 3, meetings: 1 },
  { label: "11am", calls: 0, emails: 0, convos: 0, meetings: 0 },
  { label: "12pm", calls: 0, emails: 0, convos: 0, meetings: 0 },
  { label: "1pm",  calls: 0, emails: 0, convos: 0, meetings: 0 },
];

export const BDR_CHART_WEEK = [
  { label: "Mon", calls: 8,  emails: 3, convos: 11, meetings: 2 },
  { label: "Tue", calls: 7,  emails: 2, convos:  9, meetings: 2 },
  { label: "Wed", calls: 9,  emails: 3, convos: 12, meetings: 2 },
  { label: "Thu", calls: 7,  emails: 3, convos: 10, meetings: 2 },
  { label: "Fri", calls: 7,  emails: 3, convos: 10, meetings: 1 },
];

// ─── AE Seed ─────────────────────────────────────────────────────────────────

export const AE_ACTIVITIES: AeActivityRow[] = [
  { id: "ae01", tenant_id: 1, user_id: "usr-jeff",    logged_at: ts(0, 11, 30), type: "Meeting",  account: "Bose",          account_id: 36, cp_stage: "CP3",  outcome: "Demoed intelligence layer — strong fit for quality analytics.", next_step: "Proposal by EOW",         deal_value: 410000 },
  { id: "ae02", tenant_id: 1, user_id: "usr-jeff",    logged_at: ts(0, 10, 15), type: "Email",    account: "BorgWarner",    account_id: 37, cp_stage: "CP1",  outcome: "Sent intro email with OEM case study.",                        next_step: "Follow up in 48h",        deal_value:  95000 },
  { id: "ae03", tenant_id: 1, user_id: "usr-sreejit", logged_at: ts(0,  9, 45), type: "Demo",     account: "Rivian",        account_id: 38, cp_stage: "CP3",  outcome: "Full platform demo to VP Ops — budget conversation opened.",    next_step: "Budget call next week",   deal_value: 125000 },
  { id: "ae04", tenant_id: 1, user_id: "usr-jeff",    logged_at: ts(0,  9, 10), type: "Proposal", account: "Lear",          account_id: 39, cp_stage: "CP9",  outcome: "Proposal sent to CFO and VP Engineering.",                      next_step: "Decision by May 22",      deal_value: 180000 },
  { id: "ae05", tenant_id: 1, user_id: "usr-sreejit", logged_at: ts(1, 15, 30), type: "Meeting",  account: "Bosch",         account_id: 40, cp_stage: "CP2",  outcome: "Discovery call — pain confirmed around quality gate failures.",  next_step: "Demo scheduled May 15",   deal_value:  85000 },
  { id: "ae06", tenant_id: 1, user_id: "usr-jeff",    logged_at: ts(1, 14,  0), type: "Call",     account: "Marelli",       account_id: 41, cp_stage: "CP1",  outcome: "Cold outbound connect — Director of Operations interested.",     next_step: "Send discovery questions",deal_value:  70000 },
  { id: "ae07", tenant_id: 1, user_id: "usr-sreejit", logged_at: ts(2, 11, 30), type: "Meeting",  account: "Aptiv",         account_id: 42, cp_stage: "CP4",  outcome: "Budget call — $90K confirmed, pending line-item sign-off.",     next_step: "Authority mapping",       deal_value:  90000 },
  { id: "ae08", tenant_id: 1, user_id: "usr-jeff",    logged_at: ts(2, 10,  0), type: "Email",    account: "Stellantis",    account_id: 43, cp_stage: "CP1",  outcome: "First outreach — no response yet.",                             next_step: "Follow up Friday",        deal_value: 110000 },
];

export const AE_KPI_MOCK = {
  today: {
    meetings_held:  2,
    demos_delivered: 1,
    proposals_out:  1,
    deals_advanced: 2,
    deals_closed:   0,
  },
  prior_today: {
    meetings_held:  1,
    demos_delivered: 0,
    proposals_out:  0,
    deals_advanced: 1,
    deals_closed:   0,
  },
  week: {
    meetings_held:  7,
    demos_delivered: 3,
    proposals_out:  2,
    deals_advanced: 5,
    deals_closed:   1,
  },
  prior_week: {
    meetings_held:  6,
    demos_delivered: 2,
    proposals_out:  2,
    deals_advanced: 4,
    deals_closed:   0,
  },
};

// Funnel data: deals at each CP stage
export const AE_FUNNEL_DATA = [
  { stage: "CP1", label: "CP1 — Project",      count: 4, color: "#EFEFEE" },
  { stage: "CP2", label: "CP2 — Need",         count: 3, color: "#E5E5E7" },
  { stage: "CP3", label: "CP3 — Demo",         count: 2, color: "#C9C9CC" },
  { stage: "CP4", label: "CP4 — Budget",       count: 2, color: "#8E8E94" },
  { stage: "CP5", label: "CP5 — Timeline",     count: 1, color: "#525258" },
  { stage: "CP6", label: "CP6 — Authority",    count: 1, color: "#0B0B0F" },
  { stage: "CP9", label: "CP9 — Finance",      count: 1, color: "#0B0B0F" },
  { stage: "Won", label: "Won",                count: 1, color: "#16A34A" },
];

// ─── Manager Seed ─────────────────────────────────────────────────────────────

export const MANAGER_REP_SUMMARY: ManagerRepSummary[] = [
  {
    user_id: "usr-maya",
    name: "Demo BDR",
    role: "BDR",
    tenant_id: 1,
    conversations:     10,
    meetings:           2,
    qualified_fwd:      1,
    deals_advanced:  null,
    deals_closed:    null,
    prior_conversations: 8,
    prior_meetings:      1,
    sparkline: [6, 8, 10, 7, 9, 11, 10],
  },
  {
    user_id: "usr-jeff",
    name: "Jeff Walton",
    role: "AE",
    tenant_id: 1,
    conversations:   null,
    meetings:           3,
    qualified_fwd:   null,
    deals_advanced:     3,
    deals_closed:       0,
    prior_conversations: 0,
    prior_meetings:      2,
    sparkline: [2, 3, 2, 4, 3, 3, 3],
  },
  {
    user_id: "usr-sreejit",
    name: "Sreejit Nair",
    role: "AE",
    tenant_id: 1,
    conversations:   null,
    meetings:           4,
    qualified_fwd:   null,
    deals_advanced:     2,
    deals_closed:       1,
    prior_conversations: 0,
    prior_meetings:      3,
    sparkline: [3, 2, 4, 3, 5, 4, 4],
  },
];

export const MANAGER_KPI_MOCK = {
  today: {
    team_conversations: 10,
    team_meetings_booked: 2,
    team_qualified_forward: 1,
    deals_advanced: 5,
    deals_closed: 1,
  },
  prior_today: {
    team_conversations: 8,
    team_meetings_booked: 1,
    team_qualified_forward: 1,
    deals_advanced: 4,
    deals_closed: 0,
  },
};

// ─── Executive KPI Seed (aggregate only, NO individual names) ─────────────────

export const EXECUTIVE_KPI_MOCK = {
  today: {
    org_conversations: 10,
    org_meetings_booked: 2,
    org_deals_advanced: 5,
    org_deals_closed: 1,
    pipeline_coverage: 2.8, // target 3x
  },
  prior_today: {
    org_conversations: 8,
    org_meetings_booked: 1,
    org_deals_advanced: 4,
    org_deals_closed: 0,
    pipeline_coverage: 2.4,
  },
};

export const EXECUTIVE_DAILY_SUMMARY = [
  { date: "May 8",  conversations: 10, meetings_booked: 2, deals_advanced: 5, deals_closed: 1 },
  { date: "May 7",  conversations:  8, meetings_booked: 1, deals_advanced: 4, deals_closed: 0 },
  { date: "May 6",  conversations: 12, meetings_booked: 3, deals_advanced: 6, deals_closed: 0 },
  { date: "May 5",  conversations:  9, meetings_booked: 2, deals_advanced: 3, deals_closed: 1 },
  { date: "May 4",  conversations: 11, meetings_booked: 2, deals_advanced: 5, deals_closed: 0 },
];
